import { useMemo, useState } from 'react';
import {
  ActionsDialogue, Bouton, Champ, classeListeDialogue, Dialogue, IconePlus,
  IconeRecherche, Message, Vide,
} from '@journal/ui';

/**
 * Sélecteur de contenu du programme officiel, avec recherche.
 *
 * Il sert à l'écran Programmation, qui répartit le programme sur l'année. Le
 * cahier journal a ses propres fenêtres, une par bouton « Récupérer », comme
 * l'application d'origine : elles ne partagent ni leur contenu ni leur façon
 * de se refermer.
 *
 * ── Le dialogue reste ouvert après insertion ────────────────────────────────
 * On remplit une case de programmation en y ajoutant plusieurs notions
 * d'affilée. Refermer à chaque clic imposerait de rouvrir, retrouver sa
 * recherche et se repositionner dans la liste.
 */

export interface ElementInserable {
  cle: string;
  /** Chemin d'origine : « Nombres et calculs · Comprendre les nombres ». */
  chemin: string;
  texte: string;
  /**
   * Sous-domaine dont l'objectif provient.
   *
   * La ligne du tableau est un DOMAINE ; la notion précise reste une
   * information juste, qu'on enregistre avec le bloc sans lui faire porter
   * l'affichage.
   */
  sousDomaineId?: string | null;
}

/**
 * Retire le préfixe de niveau d'un intitulé.
 *
 * Le référentiel préfixe ses notions par la classe visée — « CP-CE2 — Chercher ».
 * Utile pour filtrer, encombrant dans une liste où toutes les lignes portent le
 * même préfixe. Reprend `cleanNiv` de l'application d'origine.
 */
export function sansPrefixeNiveau(nom: string): string {
  return nom
    .replace(/^(TPS|PS|MS|GS|CP|CE1|CE2|CM1|CM2)(-(?:TPS|PS|MS|GS|CP|CE1|CE2|CM1|CM2))?\s*[—–-]\s*/i, '')
    .trim();
}

/** Normalise pour une recherche insensible à la casse ET aux accents. */
const normaliser = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Filtre par tous les termes saisis.
 *
 * Le ET plutôt que le OU : taper « fraction decimal » cherche les lignes qui
 * portent les deux, ce qui réduit vraiment la liste. Un OU la rallongerait à
 * chaque mot ajouté, à rebours de ce qu'on attend d'une recherche.
 */
export function filtrerElements(
  elements: ElementInserable[],
  recherche: string,
): ElementInserable[] {
  const termes = normaliser(recherche).split(/\s+/).filter(Boolean);
  if (termes.length === 0) return elements;
  return elements.filter((e) => {
    const texte = normaliser(`${e.chemin} ${e.texte}`);
    return termes.every((t) => texte.includes(t));
  });
}

export function SelecteurContenu({
  ouvert, surFermeture, titre, programme, surInsertion, enCours,
}: {
  ouvert: boolean;
  surFermeture: () => void;
  titre: string;
  programme: ElementInserable[];
  surInsertion: (element: ElementInserable) => void;
  enCours: boolean;
}) {
  const [recherche, setRecherche] = useState('');
  const elements = programme;
  const visibles = useMemo(
    () => filtrerElements(elements, recherche),
    [elements, recherche],
  );

  return (
    <Dialogue titre={titre} ouvert={ouvert} surFermeture={surFermeture} largeur="lg">
      <div className="flex min-h-0 flex-col gap-3">
        <Champ
          libelle="Rechercher"
          type="search"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          icone={<IconeRecherche taille={16} />}
          aide={
            recherche
              ? `${visibles.length} résultat${visibles.length > 1 ? 's' : ''}`
              : 'Plusieurs mots cherchent les lignes qui les portent tous.'
          }
          autoFocus
        />

        {elements.length === 0 ? (
          <Vide titre="Rien à insérer">
            Le programme ne propose rien pour cette matière et vos classes.
            Vérifiez les niveaux enseignés dans les réglages.
          </Vide>
        ) : visibles.length === 0 ? (
          <Message>Aucun résultat pour « {recherche} ».</Message>
        ) : (
          // La liste fait couramment plusieurs centaines de lignes : c'est
          // ELLE qui défile, dans toute la hauteur que laisse la fenêtre,
          // pendant que la recherche et « Fermer » restent en place. Plafonnée
          // à 24 rem, elle défilait dans un corps qui défilait aussi.
          <ul className={`flex flex-col gap-1 ${classeListeDialogue}`}>
            {visibles.map((element) => (
              <li key={element.cle}>
                {/*
                  * Un bouton et non un <div onClick> : la liste se parcourt à
                  * la tabulation, et Entrée insère. C'est le mode d'emploi
                  * naturel quand on vient d'écrire dans le champ de recherche.
                  */}
                <button
                  type="button"
                  disabled={enCours}
                  onClick={() => surInsertion(element)}
                  className={[
                    'group flex w-full items-start gap-2 rounded-lg border p-2.5 text-left',
                    'border-[var(--color-border)] bg-[var(--color-surface)]',
                    'transition-colors duration-150 motion-reduce:transition-none',
                    'hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]',
                    'focus-visible:outline-2 focus-visible:outline-offset-1',
                    'focus-visible:outline-[var(--color-focus)]',
                    'disabled:pointer-events-none disabled:opacity-50',
                  ].join(' ')}
                >
                  <IconePlus
                    taille={15}
                    className="mt-0.5 shrink-0 text-[var(--color-fg-muted)] group-hover:text-[var(--color-primary)]"
                  />
                  <span className="min-w-0">
                    <span className="block text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
                      {element.chemin}
                    </span>
                    <span className="mt-0.5 block text-[13px] leading-snug">
                      {element.texte}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <ActionsDialogue>
          <Bouton variante="secondary" onClick={surFermeture}>
            Fermer
          </Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}
